import { useCallback, useEffect, useReducer, useRef } from 'react';

import {
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { GLOBAL } from 'saddlebag-browser';
import logger from 'saddlebag-logger';

import withErrorBoundary from '../ErrorBoundary';
import getLocalisationHelpers from '../localisation-helpers';

import {
  ArrivalsDeparturesBoardInitialState,
  ArrivalsDeparturesBoardReducer,
} from './ArrivalsDeparturesBoardReducer';
import LocalisationContext from './Contexts/LocalisationContext';
import StringsContext from './Contexts/StringsContext';
import FlightChipGroup from './FlightChipGroup';
import FlightEntryPagination from './FlightEntryPagination';
import FlightSearch from './FlightSearch';
import Heading from './Heading';
import { BOARD_TYPE } from './server/constants';
import getLiveFlightData from './server/services/arrival-departure-svc';

import type { Board } from './ArrivalsDeparturesBoardReducer';
import type {
  ArrivalsDeparturesBoardProps,
  FlightEntryData,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

import STYLES from './ArrivalsDeparturesBoard.module.scss';

const $window = GLOBAL.getWindow();
const HORIZONTAL_NAVIGATION_HEIGHT = 48;

const ArrivalsDeparturesBoard = (props: ArrivalsDeparturesBoardProps) => {
  const [state, dispatch] = useReducer(
    ArrivalsDeparturesBoardReducer,
    ArrivalsDeparturesBoardInitialState,
  );
  const ArrivalsDeparturesBoardRef = useRef<HTMLDivElement>(null);

  const loadFlights = useCallback(
    async (useBoard?: keyof typeof BOARD_TYPE) => {
      const { iataCode, locale, market, useUMSFlightTrackerUrls } = props;
      const selectedBoard = useBoard || state.selectedBoard;

      dispatch({
        type: 'clearError',
      });

      if (selectedBoard === BOARD_TYPE.ARRIVALS) {
        dispatch({ type: 'arrivalsLoading' });
      }

      if (selectedBoard === BOARD_TYPE.DEPARTURES) {
        dispatch({ type: 'departuresLoading' });
      }

      try {
        const liveFlightData = await getLiveFlightData(
          iataCode,
          locale,
          selectedBoard,
          useUMSFlightTrackerUrls
            ? { includeFlightTrackerUrls: true, market }
            : undefined,
        );

        if (selectedBoard === BOARD_TYPE.ARRIVALS) {
          dispatch({
            type: 'arrivalsLoaded',
            payload: liveFlightData.arrivalsList,
          });
        }
        if (selectedBoard === BOARD_TYPE.DEPARTURES) {
          dispatch({
            type: 'departuresLoaded',
            payload: liveFlightData.departuresList,
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);

        logger.logWarn({
          message: 'ArrivalsDeparturesBoard: live flight data fetch failed',
          component: 'ArrivalsDeparturesBoard',
          boardType: selectedBoard,
          iataCode,
          locale,
          errorMessage,
        });
        dispatch({
          type: 'error',
        });
      }
    },
    [state, props],
  );

  const loadBoard = useCallback(
    (useBoard?: string) => {
      const { arrivalsList, departuresList } = state;
      const selectedBoard = useBoard || state.selectedBoard;

      if (
        selectedBoard === BOARD_TYPE.ARRIVALS &&
        (!arrivalsList || arrivalsList.length === 0)
      ) {
        loadFlights(selectedBoard);
      }

      if (
        selectedBoard === BOARD_TYPE.DEPARTURES &&
        (!departuresList || departuresList.length === 0)
      ) {
        loadFlights(selectedBoard);
      }
    },
    [loadFlights, state],
  );

  // The initial load should only be scheduled when the component props change.
  useEffect(() => {
    const { cldr } = props;

    dispatch({
      type: 'localisation',
      payload: getLocalisationHelpers(cldr),
    });

    const timeout = setTimeout(loadBoard, 0);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const scrollToTop = () => {
    const ArrivalsDeparturesBoardTop =
      ArrivalsDeparturesBoardRef.current?.offsetTop || 0;

    $window.scrollTo({
      top: ArrivalsDeparturesBoardTop - HORIZONTAL_NAVIGATION_HEIGHT,
      behavior: 'smooth',
    });
  };

  const onChipClick = (selectedBoard: Board) => {
    scrollToTop();
    dispatch({ type: 'clearError' });

    if (state.selectedBoard === selectedBoard || !isLoaded()) {
      return;
    }

    loadBoard(selectedBoard);
    dispatch({
      type: 'changeBoard',
      payload: selectedBoard,
    });
  };

  const onPageChange = (pageIndex: number) => {
    scrollToTop();

    if (state.currentPage === pageIndex) {
      return;
    }

    dispatch({
      type: 'changePage',
      payload: pageIndex,
    });
  };

  const getEntryList = (): FlightEntryData[] => {
    const { arrivalsList, departuresList, selectedBoard } = state;

    if (selectedBoard === BOARD_TYPE.ARRIVALS) {
      return arrivalsList;
    }

    return departuresList;
  };

  const isLoaded = () => {
    const { arrivalsLoading, departuresLoading, selectedBoard } = state;

    if (selectedBoard === BOARD_TYPE.ARRIVALS) {
      return !arrivalsLoading;
    }
    return !departuresLoading;
  };

  const codeshareContainsQuery = (
    codeshare: { airlineName: string; airlineId: string; flightNumber: string },
    query: string,
  ) =>
    codeshare.airlineName.toLowerCase().indexOf(query) !== -1 ||
    (codeshare.airlineId + codeshare.flightNumber)
      .toLowerCase()
      .indexOf(query) !== -1;

  const freeTextSearchFilter = (
    { airline, codeshares, flightCode, location, time }: FlightEntryData,
    input = state.freeTextSearch,
  ) => {
    const query = input.toLowerCase();

    return (
      airline?.toLowerCase().indexOf(query) !== -1 ||
      location?.toLowerCase().indexOf(query) !== -1 ||
      flightCode?.toLowerCase().indexOf(query) !== -1 ||
      time?.indexOf(input.toLowerCase()) !== -1 ||
      codeshares?.some((c: any) => codeshareContainsQuery(c, query))
    );
  };

  const updateFreeTextSearchValue = (value: string) => {
    dispatch({
      type: 'freeTextSearch',
      payload: value,
    });
  };

  const filterEntries = (item: FlightEntryData) => {
    const { freeTextSearch } = state;
    return !freeTextSearch || freeTextSearchFilter(item);
  };

  const {
    airportName,
    flightTrackerBasePath,
    showFlightTrackerLinks,
    strings,
    useUMSFlightTrackerUrls,
  } = props;
  const {
    currentPage,
    error,
    freeTextSearch,
    localisation,
    pageSize,
    selectedBoard,
  } = state;

  return (
    <StringsContext.Provider value={strings}>
      <LocalisationContext.Provider value={localisation}>
        {/*
          Root stays BpkVessel + SCSS for margin-bottom only: it is 6rem (>= 513px) /
          4rem (<= 512px), but BpkMarginValue accepts spacing tokens only (max
          XXXL = 4rem). BpkVessel forwards the ref used by scrollToTop. See
          backpack-api-gaps WOM-6805 Gap 1 and Gap 3.
        */}
        <BpkVessel
          as="div"
          ref={ArrivalsDeparturesBoardRef}
          data-testid="arrivals-departures-board"
          className={STYLES.ArrivalsDeparturesBoard}
        >
          <BpkBox backgroundColor="canvas-contrast">
            <BpkBox
              backgroundColor="canvas-contrast"
              marginStart={{
                base: BpkSpacing.None,
                [BpkBreakpoint.SmallTablet]: BpkSpacing.Base,
              }}
              marginEnd={{
                base: BpkSpacing.None,
                [BpkBreakpoint.SmallTablet]: BpkSpacing.Base,
              }}
              position={{
                base: 'sticky',
                [BpkBreakpoint.SmallTablet]: 'static',
              }}
              top="3rem"
              zIndex={1}
            >
              <FlightChipGroup
                airportName={airportName}
                currentBoard={selectedBoard}
                onChipClick={onChipClick}
              />
            </BpkBox>
            <BpkFlex
              justify="center"
              marginStart={BpkSpacing.Base}
              marginEnd={BpkSpacing.Base}
            >
              <BpkBox width="76.5rem" maxWidth="100%">
                <FlightSearch
                  freeTextSearch={freeTextSearch}
                  updateFreeTextSearchValue={updateFreeTextSearchValue}
                  clearSearchValue={() =>
                    dispatch({ type: 'clearSearchValue' })
                  }
                  selected={selectedBoard}
                />

                <Heading selectedBoard={selectedBoard} />

                <FlightEntryPagination
                  entries={getEntryList?.().filter(filterEntries) || []}
                  error={error}
                  onRetryButtonClick={() => loadFlights()}
                  onPageChange={onPageChange}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  showPagination
                  loaded={isLoaded()}
                  isFiltering={freeTextSearch.length > 0}
                  showFlightTrackerLinks={showFlightTrackerLinks}
                  flightTrackerBasePath={flightTrackerBasePath}
                  useUMSFlightTrackerUrls={useUMSFlightTrackerUrls}
                />
              </BpkBox>
            </BpkFlex>
          </BpkBox>
        </BpkVessel>
      </LocalisationContext.Provider>
    </StringsContext.Provider>
  );
};

const ArrivalsDeparturesBoardWithErrorBoundary = withErrorBoundary(
  ArrivalsDeparturesBoard,
  'arrivals-departures-board-component',
);
export default ArrivalsDeparturesBoardWithErrorBoundary;
